import React from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { getNameInitials } from "@/src/utils/initials";

import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  dateColumn,
  lazyActionsColumn,
  statusColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { userEditPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type { User } from "../../../../api/AdminUserAPI.types";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../../dataTable/dataTable.types";

interface UserColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  loadActions: (user: User) => Promise<RowAction[]>;
}

type UserName = Pick<User, "first_name" | "last_name">;

function getUserFullName({ first_name, last_name }: UserName): string {
  return `${first_name || ""} ${last_name || ""}`.trim();
}

function getUserLabel(user: User): string {
  return getUserFullName(user) || user.email;
}

export function getUserHref(
  user: User,
  projectSlug: string | undefined,
): string | undefined {
  return userEditPath(projectSlug, user.uuid);
}

export function userColumns({
  t,
  projectSlug,
  loadActions,
}: UserColumnsOptions): DataTableColumnDef<User>[] {
  return [
    statusColumn<User>("subscription_status", t("status"), [
      { value: "active", label: t("user_status.active"), tone: "success" },
      { value: "register", label: t("user_status.register"), tone: "info" },
      { value: "canceled", label: t("user_status.canceled"), tone: "error" },
      {
        value: "unknown",
        label: t("user_status.unknown"),
        tone: "neutral",
      },
    ]),
    {
      id: "photo",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("avatar")} />
      ),
      enableSorting: false,
      meta: { label: t("avatar"), width: "7%", minWidth: 70, align: "center" },
      cell: ({ row }) => (
        <Avatar className="inline-flex" size="sm">
          <AvatarImage alt="" src={row.original.avatar_url} />
          <AvatarFallback>
            {getNameInitials(row.original.first_name, row.original.last_name)}
          </AvatarFallback>
        </Avatar>
      ),
    },
    {
      id: "last_name",
      accessorFn: (user) => getUserFullName(user),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      meta: { label: t("name"), searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell href={getUserHref(row.original, projectSlug)}>
          {getUserFullName(row.original)}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("email")} />
      ),
      meta: { label: t("email"), searchable: true },
    },
    {
      accessorKey: "views_count",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("views_count")} />
      ),
      // Le serveur ne trie pas sur les vues.
      enableSorting: false,
      meta: {
        label: t("views_count"),
        width: "10%",
        minWidth: 95,
        align: "center",
      },
      cell: ({ row }) => (row.original.views_count ?? 0).toLocaleString(),
    },
    dateColumn<User>("inserted_at", t("registered_on"), {
      width: "18%",
      minWidth: 220,
    }),
    dateColumn<User>("updated_at", t("updated_at"), {
      width: "15%",
      minWidth: 185,
    }),
    lazyActionsColumn<User>(loadActions, getUserLabel),
  ];
}
